import type {
  User,
  UserFormData,
  VerificationFormData,
  AuthResponse,
} from '../types/user';
import { api } from '../../../shared/api/axios';
import { tokenManager } from '../../../shared/api/tokenManager';
import { logError } from '../../../shared/utils/errorUtils'

/**
 * Sign in with email and password.
 * Stores the access token in memory and returns full AuthResponse.
 */
export const signIn = async (
  email: string,
  password: string,
  rememberMe: boolean = false
): Promise<AuthResponse> => {
  try {
    const response = await api.post<AuthResponse>('/auth/login', {
      email,
      password,
      remember_me: rememberMe,
    });
    return response;
  } catch (error: unknown) {
    logError(error, 'signIn');
    throw error;
  }
};

/**
 * Register a new user.
 */
export const signUp = async (
  data: UserFormData,
  queryParams?: string
): Promise<void> => {
  try {
    const url = queryParams
      ? `/auth/register?${queryParams}`
      : '/auth/register';
    await api.post(url, data);
  } catch (error) {
    logError(error, 'Registration Failed');
    throw error;
  }
};

/**
 * Sign out the current user.
 * Clears in-memory token and notifies backend to revoke session.
 */
export const signOut = async (): Promise<void> => {
  try {
    await api.post('/auth/logout', {}, { withCredentials: true });
    tokenManager.clearTokens();
  } catch (error) {
    logError(error, 'signOut');
    tokenManager.clearTokens();
    throw error;
  }
};

/**
 * Attempt a silent refresh
 * Returns only the new access token and expiry, or null if it fails.
 * Note: This endpoint does NOT return user data - you must call getProfile()
 * separately after successful token refresh to get updated user information.
 */
export const silentRefresh = async (): Promise<{
  access_token: string;
  expires_in: number;
} | null> => {
  try {
    const response = await api.post<{
      access_token: string;
    }>('/auth/token/refresh', {}, { withCredentials: true, timeout: 5000 });
    if (!response.access_token) {
      return null;
    }
    // Backend only returns access_token on refresh, use default 5 min TTL
    return { access_token: response.access_token, expires_in: 300 };
  } catch (error) {
    // Silent refresh failures are expected when no session exists - don't log as error
    console.log('Silent refresh: No active session');
    return null;
  }
};

/**
 * Fetch the current user's profile.
 */
export const getProfile = async (): Promise<User> => {
  try {
    const response = await api.get<User>('/users/me');
    return response;
  } catch (error) {
    logError(error, 'getProfile');
    throw error;
  }
};

/**
 * Update the current user's profile.
 */
export const updateProfile = async (data: Partial<User>): Promise<User> => {
  try {
    return await api.patch<User>('/users/me', data);
  } catch (error) {
    logError(error, 'updateProfile');
    throw error;
  }
};

/**
 * Submit operator verification documents.
 */
export const submitVerification = async (
  data: VerificationFormData
): Promise<User> => {
  try {
    const formData = new FormData();
    formData.append('company_name', data.company_name);
    formData.append('phone_number', data.phone_number);
    if (data.business_license) {
      formData.append('business_license', data.business_license);
    }
    if (data.identification_doc) {
      formData.append('identification_doc', data.identification_doc);
    }
    if (data.insurance_cert) {
      formData.append('insurance_cert', data.insurance_cert);
    }

    const response = await api.post<User>('/auth/verify', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  } catch (error) {
    logError(error, 'submitVerification');
    throw error;
  }
};

/**
 * Sign in with Google OAuth.
 * For travelers only - requires id_token from Google.
 */
export const signInWithGoogle = async (
  idToken: string,
  rememberMe: boolean = false
): Promise<AuthResponse> => {
  try {
    const response = await api.post<AuthResponse>('/auth/google', {
      id_token: idToken,
      remember_me: rememberMe,
    });
    return response;
  } catch (error: unknown) {
    logError(error, 'signInWithGoogle');
    throw error;
  }
};

/**
 * Verify Google OAuth token.
 */
export const verifyGoogleToken = async (idToken: string): Promise<boolean> => {
  try {
    await api.post('/auth/google/verify', { id_token: idToken });
    return true;
  } catch (error) {
    logError(error, 'verifyGoogleToken');
    return false;
  }
};
